import { useState, useEffect } from 'react';
import {
  ShieldCheck,
  Clock,
  CheckCircle,
  XCircle,
  AlertTriangle,
  RefreshCw,
  ChevronRight,
  User,
} from 'lucide-react';
import './Approvals.css';

interface Approval {
  id: string;
  discordMessageId: string;
  actionType: string;
  actionDetails: Record<string, unknown>;
  status: 'pending' | 'approved' | 'rejected' | 'expired';
  requestedAt: string;
  resolvedAt?: string;
  resolvedBy?: string;
}

interface ApprovalStats {
  total: number;
  pending: number;
  approved: number;
  rejected: number;
  expired: number;
  recentPending: number;
  byActionType: Record<string, number>;
}

const defaultStats: ApprovalStats = {
  total: 0,
  pending: 0,
  approved: 0,
  rejected: 0,
  expired: 0,
  recentPending: 0,
  byActionType: {},
};

export default function Approvals() {
  const [approvals, setApprovals] = useState<Approval[]>([]);
  const [stats, setStats] = useState<ApprovalStats>(defaultStats);
  const [actionTypes, setActionTypes] = useState<string[]>([]);
  const [selectedApproval, setSelectedApproval] = useState<Approval | null>(null);
  const [statusFilter, setStatusFilter] = useState<string>('all');
  const [actionTypeFilter, setActionTypeFilter] = useState<string>('all');
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [updating, setUpdating] = useState(false);

  useEffect(() => {
    fetchData();
  }, []);

  const fetchData = async () => {
    setLoading(true);
    setError(null);
    try {
      const [approvalsRes, statsRes, typesRes] = await Promise.all([
        fetch('/api/approvals?limit=100'),
        fetch('/api/approvals/stats'),
        fetch('/api/approvals/action-types'),
      ]);

      if (approvalsRes.ok) {
        const data = await approvalsRes.json();
        setApprovals(data.approvals || []);
      }
      if (statsRes.ok) {
        const data = await statsRes.json();
        setStats(data);
      }
      if (typesRes.ok) {
        const data = await typesRes.json();
        setActionTypes(data.actionTypes || []);
      }
    } catch (err) {
      console.error('Failed to fetch approvals:', err);
      setError('Failed to load approvals');
    } finally {
      setLoading(false);
    }
  };

  const handleApproval = async (approvalId: string, status: 'approved' | 'rejected') => {
    setUpdating(true);
    try {
      const res = await fetch(`/api/approvals/${approvalId}`, {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ status, resolvedBy: 'web-ui' }),
      });
      if (res.ok) {
        fetchData();
        if (selectedApproval?.id === approvalId) {
          const updated = await res.json();
          setSelectedApproval(updated);
        }
      }
    } catch (err) {
      console.error('Failed to update approval:', err);
    } finally {
      setUpdating(false);
    }
  };

  const filteredApprovals = approvals.filter((approval) => {
    const matchesStatus = statusFilter === 'all' || approval.status === statusFilter;
    const matchesType = actionTypeFilter === 'all' || approval.actionType === actionTypeFilter;
    return matchesStatus && matchesType;
  });

  const formatDate = (dateStr: string) => {
    const date = new Date(dateStr);
    const now = new Date();
    const diffMs = now.getTime() - date.getTime();
    const diffHours = diffMs / (1000 * 60 * 60);

    if (diffHours < 1) {
      const diffMins = Math.floor(diffMs / (1000 * 60));
      return `${diffMins}m ago`;
    } else if (diffHours < 24) {
      return `${Math.floor(diffHours)}h ago`;
    } else {
      return date.toLocaleDateString() + ' ' + date.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' });
    }
  };

  const getStatusIcon = (status: string) => {
    switch (status) {
      case 'pending':
        return <Clock size={16} className="status-icon pending" />;
      case 'approved':
        return <CheckCircle size={16} className="status-icon approved" />;
      case 'rejected':
        return <XCircle size={16} className="status-icon rejected" />;
      case 'expired':
        return <AlertTriangle size={16} className="status-icon expired" />;
      default:
        return null;
    }
  };

  const formatActionType = (type: string) => {
    return type
      .split('_')
      .map((word) => word.charAt(0).toUpperCase() + word.slice(1))
      .join(' ');
  };

  if (loading) {
    return (
      <div className="page-container">
        <div className="page-header">
          <h1>Approvals</h1>
        </div>
        <div className="loading-state">Loading approvals...</div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="page-container">
        <div className="page-header">
          <h1>Approvals</h1>
        </div>
        <div className="error-state">{error}</div>
      </div>
    );
  }

  return (
    <div className="page-container approvals-page">
      {/* Header */}
      <div className="page-header">
        <div className="header-content">
          <h1>Approvals</h1>
          <p>Manage pending approval requests for destructive operations</p>
        </div>
        <button className="btn btn-secondary" onClick={fetchData}>
          <RefreshCw size={16} />
          Refresh
        </button>
      </div>

      {/* Stats */}
      <div className="approvals-stats">
        <div className="approval-stat pending">
          <div className="approval-stat-icon">
            <Clock size={20} />
          </div>
          <div className="approval-stat-content">
            <div className="approval-stat-value">{stats.pending}</div>
            <div className="approval-stat-label">Pending</div>
          </div>
        </div>
        <div className="approval-stat approved">
          <div className="approval-stat-icon">
            <CheckCircle size={20} />
          </div>
          <div className="approval-stat-content">
            <div className="approval-stat-value">{stats.approved}</div>
            <div className="approval-stat-label">Approved</div>
          </div>
        </div>
        <div className="approval-stat rejected">
          <div className="approval-stat-icon">
            <XCircle size={20} />
          </div>
          <div className="approval-stat-content">
            <div className="approval-stat-value">{stats.rejected}</div>
            <div className="approval-stat-label">Rejected</div>
          </div>
        </div>
        <div className="approval-stat">
          <div className="approval-stat-icon">
            <ShieldCheck size={20} />
          </div>
          <div className="approval-stat-content">
            <div className="approval-stat-value">{stats.total}</div>
            <div className="approval-stat-label">Total</div>
          </div>
        </div>
      </div>

      {/* Filters */}
      <div className="approvals-toolbar">
        <div className="filter-group">
          <select
            value={statusFilter}
            onChange={(e) => setStatusFilter(e.target.value)}
            className="filter-select"
          >
            <option value="all">All Status</option>
            <option value="pending">Pending</option>
            <option value="approved">Approved</option>
            <option value="rejected">Rejected</option>
            <option value="expired">Expired</option>
          </select>
          <select
            value={actionTypeFilter}
            onChange={(e) => setActionTypeFilter(e.target.value)}
            className="filter-select"
          >
            <option value="all">All Action Types</option>
            {actionTypes.map((type) => (
              <option key={type} value={type}>
                {formatActionType(type)}
              </option>
            ))}
          </select>
        </div>
      </div>

      {/* Content */}
      <div className="approvals-content">
        {/* Approvals List */}
        <div className="approvals-list">
          {filteredApprovals.length === 0 ? (
            <div className="empty-state">
              <ShieldCheck size={48} />
              <h3>No approvals found</h3>
              <p>
                {statusFilter === 'pending'
                  ? 'No pending approvals at this time'
                  : 'Try adjusting your filters'}
              </p>
            </div>
          ) : (
            filteredApprovals.map((approval) => (
              <div
                key={approval.id}
                className={`approval-item ${selectedApproval?.id === approval.id ? 'selected' : ''} ${approval.status}`}
                onClick={() => setSelectedApproval(approval)}
              >
                <div className="approval-status-indicator">
                  {getStatusIcon(approval.status)}
                </div>
                <div className="approval-content">
                  <div className="approval-header">
                    <span className="approval-type">{formatActionType(approval.actionType)}</span>
                    <span className="approval-time">{formatDate(approval.requestedAt)}</span>
                  </div>
                  <div className="approval-description">
                    {approval.actionDetails.description as string ||
                     `${approval.actionType} operation requested`}
                  </div>
                  <div className="approval-footer">
                    <span className={`status-badge ${approval.status}`}>
                      {approval.status}
                    </span>
                    {approval.resolvedBy && (
                      <span className="resolved-by">
                        <User size={12} />
                        {approval.resolvedBy}
                      </span>
                    )}
                  </div>
                </div>
                <ChevronRight size={16} className="approval-chevron" />
              </div>
            ))
          )}
        </div>

        {/* Detail Panel */}
        {selectedApproval && (
          <div className="approval-detail-panel">
            <div className="approval-detail-header">
              <div className="approval-detail-title">
                {getStatusIcon(selectedApproval.status)}
                <h2>{formatActionType(selectedApproval.actionType)}</h2>
              </div>
              <button className="close-btn" onClick={() => setSelectedApproval(null)}>×</button>
            </div>

            <div className="approval-detail-body">
              <div className="detail-section">
                <div className="detail-row">
                  <span className="detail-label">Status</span>
                  <span className={`status-badge ${selectedApproval.status}`}>
                    {selectedApproval.status}
                  </span>
                </div>
                <div className="detail-row">
                  <span className="detail-label">Requested</span>
                  <span className="detail-value">
                    {new Date(selectedApproval.requestedAt).toLocaleString()}
                  </span>
                </div>
                {selectedApproval.resolvedAt && (
                  <div className="detail-row">
                    <span className="detail-label">Resolved</span>
                    <span className="detail-value">
                      {new Date(selectedApproval.resolvedAt).toLocaleString()}
                    </span>
                  </div>
                )}
                {selectedApproval.resolvedBy && (
                  <div className="detail-row">
                    <span className="detail-label">Resolved By</span>
                    <span className="detail-value">{selectedApproval.resolvedBy}</span>
                  </div>
                )}
                <div className="detail-row">
                  <span className="detail-label">Discord Message</span>
                  <span className="detail-value mono">{selectedApproval.discordMessageId}</span>
                </div>
              </div>

              <div className="detail-section">
                <h3>Action Details</h3>
                <pre className="action-details">
                  {JSON.stringify(selectedApproval.actionDetails, null, 2)}
                </pre>
              </div>

              {selectedApproval.status === 'pending' && (
                <div className="approval-actions">
                  <button
                    className="btn btn-danger"
                    onClick={() => handleApproval(selectedApproval.id, 'rejected')}
                    disabled={updating}
                  >
                    <XCircle size={14} />
                    Reject
                  </button>
                  <button
                    className="btn btn-success"
                    onClick={() => handleApproval(selectedApproval.id, 'approved')}
                    disabled={updating}
                  >
                    <CheckCircle size={14} />
                    Approve
                  </button>
                </div>
              )}
            </div>
          </div>
        )}
      </div>
    </div>
  );
}
